import React, { useState, useEffect, useCallback, Suspense, useContext } from "react";
import { Link, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../../../services/interceptor";
import Select from "react-select";
import Loading from "../../Loading";
import ToothGrid from "../Comman/ToothGrid";
import AllergiesSection from "../Comman/MedicalForm/AllergiesSection";
import PatientReportPDF from "./PatientReportPDF";
import Dropdown from "../Comman/Dropdown";
import { AuthContext } from "../../../context/AuthContext";
import BillingReportHTML from "./Prints/BillingReportHTML";
import PaymentMemoHTML from "./Prints/PaymentMemoHTML";
import BillingItems from "./BillingItems";
import { Eye, Printer } from "lucide-react";
import AddCategoryItemModal from "../Comman/AddCategoryItemModal";

const CheckupScroll = () => {
  const { medicationOptions, primaryDiagnosis, secondaryDiagnosis, treatmentPlan, visitReason, medicineName, timeBlocks, proceduresPerformedToday, documentName } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("allergiesName");
  const autosaveTimerRef = React.useRef(null);

  const { id } = useParams();

  // Loading states
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Success and error messages
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [opdForm, setOPDForm] = useState({});
  const [selectedTeeth, setSelectedTeeth] = useState([]);
  const [enableClinicalNotes, setEnableClinicalNotes] = useState(false);

  // Corrected Patient Report State with proper initialization
  const [patientReport, setPatientReport] = useState({
    opdNumber: "",
    examinationFindings: {
      teethCondition: "",
      gumsCondition: "",
      allergies: [],
      currentMedications: [],
      oralHygieneLevel: "",
      notes: "",
      selectedTeeth: [
        {
          id: {
            type: Number,
          },
          reasons: [{
            type: String,
            default: [],
          }],
          diagnosis: {
            type: String,
          },
          treatment: {
            type: String,
          },

        },
      ],
    },
    diagnosis: {
      primaryDiagnosis: "",
      secondaryDiagnosis: "",
    },
    treatmentPlan: {
      treatmentSuggested: "",
      immediateProceduresPerformed: "",
      prescribedMedications: [],
    },
    additionalNotes: "",
    financials: {
      billingItems: [

      ],
      totalCost: 0,
      visits: [
        {
          treatmentDone: false,
          date: "",
          reason: "",
          amount: 0,
          notes: "",
        },
      ],
    },
    documents: [
      {
        name: "",
        url: "",
        customName: "",
      },
    ],
    createdAt: "",
  });

  //prescribed medications state
  const [showReport, setShowReport] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);


  const generatePatientReportHTML = () => {
    try {
      setShowReport(true);
    } catch (error) {
      console.error("Error showing report:", error);
    }
  };
  const generateReport = () => setShowInvoice(true);




  // Reset unsaved changes flag after successful save
  const resetUnsavedChanges = () => {
    setHasUnsavedChanges(false);
  };

  // Fetch existing Patient Report data
  useEffect(() => {
    const fetchPatientReport = async () => {
      try {
        const response = await API.get(`/api/doctor/details/${id}`);

        setOPDForm(response?.data);

        // Ensure proper data structure initialization
        const reportData = response.data.patientReport || {};

        // Initialize with default structure if data is missing
        const initializedReport = {
          opdNumber: reportData.opdNumber || "",
          examinationFindings: {
            teethCondition:
              reportData.examinationFindings?.teethCondition || "",
            gumsCondition: reportData.examinationFindings?.gumsCondition || "",
            allergies:
              reportData.examinationFindings?.allergies?.length > 0
                ? reportData.examinationFindings.allergies
                : [],
            currentMedications:
              reportData.examinationFindings?.currentMedications?.length > 0
                ? reportData.examinationFindings.currentMedications.map((med) =>
                  typeof med === "string" ? { value: med, label: med } : med
                )
                : [],

            oralHygieneLevel:
              reportData.examinationFindings?.oralHygieneLevel || "",
            notes: reportData.examinationFindings?.notes || "",
            selectedTeeth: reportData.examinationFindings?.selectedTeeth || [],
          },
          diagnosis: {
            primaryDiagnosis: reportData.diagnosis?.primaryDiagnosis || "",
            secondaryDiagnosis: reportData.diagnosis?.secondaryDiagnosis || "",
          },
          treatmentPlan: {
            treatmentSuggested:
              reportData.treatmentPlan?.treatmentSuggested || "",
            immediateProceduresPerformed:
              reportData.treatmentPlan?.immediateProceduresPerformed || "",
            prescribedMedications:
              reportData.treatmentPlan?.prescribedMedications?.length > 0
                ? reportData.treatmentPlan.prescribedMedications.map((med) => ({
                  name: med.name || "",
                  dosage: med.dosage || "",
                  mealTiming: med.mealTiming || "",
                  timeOfDay: Array.isArray(med.timeOfDay)
                    ? med.timeOfDay
                    : [],
                  durationDays: med.durationDays || "",
                }))
                : [],
          },
          additionalNotes: reportData.additionalNotes || "",
          financials: {
            billingItems:
              reportData.financials?.billingItems?.length > 0
                ? reportData.financials.billingItems
                : [],
            totalCost: reportData.financials?.totalCost || 0,
            visits:
              reportData.financials?.visits?.length > 0
                ? reportData.financials.visits
                : [
                  {
                    treatmentDone: false,
                    date: new Date().toISOString().split('T')[0],
                    reason: "",
                    amount: 0,
                    notes: "",
                  },
                ],
          },
          documents:
            reportData.documents?.length > 0
              ? reportData.documents.map((doc) => ({
                name: doc.name || "",
                url: doc.url || "",
                customName: doc.customName || "",
              }))
              : [],
          //createdAt: reportData.createdAt || "",
        };

        setPatientReport(initializedReport);

        // Handle selectedTeeth with backward compatibility
        const loadedSelectedTeeth =
          initializedReport.examinationFindings.selectedTeeth;

        // Convert old format to new format if needed
        const convertedTeeth = loadedSelectedTeeth.map((tooth) => {
          if (tooth.reason && !tooth.reasons) {
            return {
              id: tooth.id,
              reasons: [tooth.reason],
            };
          } else if (tooth.reasons) {
            return tooth;
          } else {
            return {
              id: tooth.id,
              reasons: [],
            };
          }
        });

        setSelectedTeeth(convertedTeeth);
        setInitialLoading(false);
      } catch (err) {
        console.error("Error fetching Patient Report:", err.message);
        setError(
          err.response?.data?.message ||
          "Failed to load the patient report. Please try again."
        );
        setInitialLoading(false);
      }
    };

    fetchPatientReport();
  }, [id]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    const handlePopState = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        setShowExitModal(true);
        setPendingNavigation("back");
        // Push current state back to prevent immediate navigation
        window.history.pushState(null, "", window.location.pathname);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Push initial state to handle back button
    window.history.pushState(null, "", window.location.pathname);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnsavedChanges]);




  // Modal handlers
  const handleSaveAndExit = async () => {
    try {
      setLoading(true);

      // Call handleSubmit directly instead of trying to submit form programmatically
      await handleSubmit({ preventDefault: () => { } });

      setShowExitModal(false);
      setHasUnsavedChanges(false);

      // Navigate after save
      if (pendingNavigation === "back") {
        window.history.back();
      }
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExitWithoutSaving = () => {
    setHasUnsavedChanges(false);
    setShowExitModal(false);

    if (pendingNavigation === "back") {
      window.history.back();
    }
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
    setPendingNavigation(null);
  };

  // const medicationOptions = [
  //   { value: "Paracetamol", label: "Paracetamol" },
  //   { value: "Amoxicillin", label: "Amoxicillin" },
  //   { value: "Ibuprofen", label: "Ibuprofen" },
  //   { value: "Metronidazole", label: "Metronidazole" },
  //   { value: "Aspirin", label: "Aspirin" },
  //   { value: "Cephalexin", label: "Cephalexin" },
  //   { value: "Clindamycin", label: "Clindamycin" },
  //   { value: "Diclofenac", label: "Diclofenac" },
  //   { value: "Prednisolone", label: "Prednisolone" },
  //   { value: "Tramadol", label: "Tramadol" },
  //   { value: "Codeine", label: "Codeine" },
  //   { value: "Azithromycin", label: "Azithromycin" },
  //   // Add more as needed
  // ];

  // In your component state, add:
  const [selectedMedications, setSelectedMedications] = useState([]);

  // Handler for multi-select
  const handleMedicationSelect = (selectedOptions) => {
    setSelectedMedications(selectedOptions || []);
    // Optionally, update patientReport.treatmentPlan.prescribedMedications here
  };

  const handleCurrentMedicationSelect = (selectedOptions) => {
    setPatientReport((prevState) => ({
      ...prevState,
      examinationFindings: {
        ...prevState.examinationFindings,
        currentMedications: selectedOptions || [],
      },
    }));
  };

  // Corrected handleMedicationChange function
  const handleMedicationChange = (e, index, field) => {
    const value = e.target.value;

    setPatientReport((prevState) => {
      const meds = [...prevState.treatmentPlan.prescribedMedications];

      // Handle timeOfDay array properly
      if (field === "timeOfDay") {
        meds[index][field] = value; // value should already be an array
      } else {
        meds[index][field] = value;
      }

      return {
        ...prevState,
        treatmentPlan: {
          ...prevState.treatmentPlan,
          prescribedMedications: meds,
        },
      };
    });
  };

  // Medication Handlers - Updated
  const addMedication = () => {
    setPatientReport((prevState) => ({
      ...prevState,
      treatmentPlan: {
        ...prevState.treatmentPlan,
        prescribedMedications: [
          ...(prevState.treatmentPlan.prescribedMedications || []), // Ensure array exists
          {
            name: "",
            dosage: "",
            mealTiming: "",
            timeOfDay: [],
            durationDays: "",
          },
        ],
      },
    }));
  };

  const removeMedication = (index) => {
    setPatientReport((prevState) => {
      const meds = [...(prevState.treatmentPlan.prescribedMedications || [])];
      meds.splice(index, 1);
      return {
        ...prevState,
        treatmentPlan: {
          ...prevState.treatmentPlan,
          prescribedMedications: meds, // This will be an empty array [] when all medications are removed
        },
      };
    });
  };

  // Corrected handleDocumentUpload function
  const handleDocumentUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "mypreset");
    formData.append("cloud_name", "dsi1mb2aj");

    // Create a unique toast ID for this upload
    const uploadToastId = `upload-${index}-${Date.now()}`;

    // Show loading toast
    toast.loading("Document Uploading...", {
      position: "top-center",
      toastId: uploadToastId,
    });

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/dsi1mb2aj/image/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const documentUrl = data.secure_url;

      setPatientReport((prevState) => {
        const updatedDocuments = [...prevState.documents];
        updatedDocuments[index] = {
          ...updatedDocuments[index],
          url: documentUrl,
        };
        return { ...prevState, documents: updatedDocuments };
      });

      // Dismiss loading toast
      toast.dismiss(uploadToastId);

      // Show success toast
      toast.success("Document Uploaded Successfully", {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error uploading document:", error);

      // Dismiss loading toast
      toast.dismiss(uploadToastId);

      // Show error toast
      toast.error("Failed to upload document", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  // Corrected handleNestedChange function
  const handleNestedChange = (
    e,
    section,
    field,
    index = null,
    subField = null
  ) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    setPatientReport((prevState) => {
      if (section === "documents") {
        const updatedDocuments = [...prevState.documents];
        if (index !== null && subField) {
          updatedDocuments[index] = {
            ...updatedDocuments[index],
            [subField]: value,
          };
        }
        return { ...prevState, documents: updatedDocuments };
      }

      const updatedSection = { ...prevState[section] };

      if (section === "examinationFindings" || section === "diagnosis") {
        updatedSection[field] = value;
      } else if (section === "treatmentPlan") {
        if (field === "prescribedMedications" && index !== null && subField) {
          const meds = [...updatedSection.prescribedMedications];
          meds[index] = {
            ...meds[index],
            [subField]: value,
          };
          updatedSection.prescribedMedications = meds;
        } else {
          updatedSection[field] = value;
        }
      } else if (section === "financials") {
        if (field === "visits" && index !== null && subField) {
          const visits = [...updatedSection.visits];
          visits[index] = {
            ...visits[index],
            [subField]: value,
          };
          updatedSection.visits = visits;
        } else {
          updatedSection[field] = value;
        }
      }

      return { ...prevState, [section]: updatedSection };
    });
  };

  // Corrected handleBillingItemChange function
  const handleBillingItemChange = (e, index, field) => {
    const value = e.target.value;

    setPatientReport((prevState) => {
      const updatedBillingItems = [...prevState.financials.billingItems];

      // Update the specific field
      updatedBillingItems[index] = {
        ...updatedBillingItems[index],
        [field]:
          field === "quantity" || field === "pricePerItem"
            ? parseFloat(value) || 0
            : value,
      };

      // Auto-fill price when treatment name is selected
      if (field === "name") {
        const selectedTreatment = treatmentPlan.find(tp => tp.item === value);
        if (selectedTreatment) {
          updatedBillingItems[index].pricePerItem = selectedTreatment.price;
        }
      }

      // Recalculate totalCostPerItem for that item
      updatedBillingItems[index].totalCostPerItem =
        updatedBillingItems[index].quantity *
        updatedBillingItems[index].pricePerItem;

      // Calculate total cost
      const totalCost = updatedBillingItems.reduce(
        (acc, item) => acc + (item.totalCostPerItem || 0),
        0
      );

      return {
        ...prevState,
        financials: {
          ...prevState.financials,
          billingItems: updatedBillingItems,
          totalCost: totalCost,
        },
      };
    });
  };

  // Handle changes to selectedTeeth
  useEffect(() => {
    setPatientReport((prevState) => ({
      ...prevState,
      examinationFindings: {
        ...prevState.examinationFindings,
        selectedTeeth,
      },
    }));
  }, [selectedTeeth]);

  // Handle changes for simple fields
  const handleSimpleChange = (e) => {
    const { name, value } = e.target;
    setPatientReport((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Financial Visits Handlers

  const addVisit = () => {
    setPatientReport((prevState) => ({
      ...prevState,
      financials: {
        ...prevState.financials,
        visits: [
          ...(prevState.financials.visits || []), // Ensure visits array exists
          {
            treatmentDone: false,
            date: "",
            reason: "",
            amount: 0,
            notes: "",
          },
        ],
      },
    }));
  };

  const removeVisit = (index) => {
    // Ask for PIN before deleting
    const pin = window.prompt("Enter PIN to confirm deletion:");

    // If user cancelled or entered wrong PIN, abort
    if (pin === null) {
      return; // user cancelled
    }
    if (pin !== "2025") {
      window.alert("Incorrect PIN. Deletion cancelled.");
      return;
    }

    // PIN is correct — proceed to remove item
    setPatientReport((prevState) => {
      const visits = [...(prevState.financials.visits || [])];
      visits.splice(index, 1);

      return {
        ...prevState,
        financials: {
          ...prevState.financials,
          visits: visits,
        },
      };
    });
  };


  // Documents Handlers - Updated
  const addDocument = () => {
    setPatientReport((prevState) => ({
      ...prevState,
      documents: [
        ...(prevState.documents || []), // Ensure documents array exists
        { name: "", url: "", customName: "" },
      ],
    }));
  };

  const removeDocument = (index) => {
    setPatientReport((prevState) => {
      const docs = [...(prevState.documents || [])];
      docs.splice(index, 1);
      return {
        ...prevState,
        documents: docs, // This will be an empty array [] when all documents are removed
      };
    });
  };

  // Billing Items Handlers - Updated

  const addBillingItem = () => {
    setPatientReport((prevState) => ({
      ...prevState,
      financials: {
        ...prevState.financials,
        billingItems: [
          ...(prevState.financials.billingItems || []), // Ensure billingItems array exists
          { name: "", quantity: 1, pricePerItem: 0, totalCostPerItem: 0 },
        ],
      },
    }));
  };

  const removeBillingItem = (index) => {
    // Ask for PIN before deleting
    const pin = window.prompt("Enter PIN to confirm deletion:");

    // If user cancelled prompt (pin === null) or entered wrong PIN, abort
    if (pin === null) {
      // user cancelled
      return;
    }
    if (pin !== "2025") {
      window.alert("Incorrect PIN. Deletion cancelled.");
      return;
    }

    // PIN is correct — proceed to remove item
    setPatientReport((prevState) => {
      const updatedBillingItems = [...(prevState.financials.billingItems || [])];
      updatedBillingItems.splice(index, 1);

      // Recalculate totalCost
      const totalCost = updatedBillingItems.reduce(
        (acc, item) => acc + (item.totalCostPerItem || 0),
        0
      );

      return {
        ...prevState,
        financials: {
          ...prevState.financials,
          billingItems: updatedBillingItems,
          totalCost: totalCost,
        },
      };
    });
  };


  // Allergy Handlers - Updated
  const handleAllergiesChange = useCallback((section, index, field, value) => {
    setPatientReport((prevState) => ({
      ...prevState,
      examinationFindings: {
        ...prevState.examinationFindings,
        [section]: prevState.examinationFindings[section].map((item, idx) =>
          idx === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  }, []);

  // Add allergy entry
  const addAllergy = useCallback(() => {
    setPatientReport((prevState) => ({
      ...prevState,
      examinationFindings: {
        ...prevState.examinationFindings,
        allergies: [
          ...(prevState.examinationFindings.allergies || []), // Ensure array exists
          { allergen: "", reaction: "", severity: "" },
        ],
      },
    }));
  }, []);

  // Remove allergy entry
  const removeAllergy = useCallback((index) => {
    setPatientReport((prevState) => ({
      ...prevState,
      examinationFindings: {
        ...prevState.examinationFindings,
        allergies: prevState.examinationFindings.allergies.filter(
          (_, idx) => idx !== index
        ),
      },
    }));
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Create a unique toast ID for this submission
    const loadingToastId = `patient-report-${Date.now()}`;

    // Show loading toast
    toast.loading("Updating Patient Report...", {
      position: "top-center",
      toastId: loadingToastId,
    });

    try {
      // Prepare data for API - convert currentMedications to simple array
      const apiData = {
        ...patientReport,
        examinationFindings: {
          ...patientReport.examinationFindings,
          currentMedications:
            patientReport.examinationFindings.currentMedications?.map((med) =>
              typeof med === "object" ? med.value : med
            ) || [],
        },
      };

      console.log("Submitting Patient Report Data:", apiData);
      await API.put(`/api/doctor/patient-reports/${id}`, apiData);

      resetUnsavedChanges();

      // Dismiss the loading toast
      toast.dismiss(loadingToastId);

      // Show success toast with a different ID
      toast.success("Patient Report Updated Successfully", {
        position: "top-center",
        autoClose: 3000,
      });

      setSuccess("Patient Report updated successfully!");
      setError("");
    } catch (err) {
      console.error(err);

      // Dismiss the loading toast
      toast.dismiss(loadingToastId);

      // Show error toast with a different ID
      toast.error("Failed To Update Patient Report", {
        position: "top-center",
        autoClose: 3000,
      });

      setError(
        err.response?.data?.message ||
        "Failed to update the patient report. Please try again."
      );
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only autosave when there are unsaved changes
    if (!hasUnsavedChanges) return;

    // Clear existing timer
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(async () => {
      try {
        // Prepare apiData the same way you do in handleSubmit
        const apiData = {
          ...patientReport,
          examinationFindings: {
            ...patientReport.examinationFindings,
            currentMedications:
              patientReport.examinationFindings.currentMedications?.map((med) =>
                typeof med === "object" ? med.value : med
              ) || [],
          },
        };

        await API.put(`/api/doctor/patient-reports/${id}`, apiData);
        // reset only if you want to consider autosave as "saved"
        resetUnsavedChanges();
        console.log("Autosaved patient report");
      } catch (error) {
        console.error("Failed to autosave patient report:", error);
      }
    }, 15000); // 15s debounce

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [patientReport, hasUnsavedChanges, id]); // run when report or dirty flag changes


  // 7. Optional: Add a helper function to clear all current medications
  const clearAllCurrentMedications = () => {
    setPatientReport((prevState) => ({
      ...prevState,
      examinationFindings: {
        ...prevState.examinationFindings,
        currentMedications: [],
      },
    }));
  };

  // Helper function to display teeth with multiple reasons
  const renderSelectedTeethWithReasons = () => {
    if (selectedTeeth.length === 0) return null;

    return (
      <div className="mt-8">
        <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-4 h-4 text-blue-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-slate-900">
              Selected Teeth for Treatment
            </h3>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              {selectedTeeth.length}{" "}
              {selectedTeeth.length === 1 ? "tooth" : "teeth"}
            </span>
          </div>

          {selectedTeeth.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedTeeth.map((tooth) => (
                <div
                  key={tooth.id}
                  className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-semibold">
                        {tooth.id}
                      </div>
                      <span className="font-medium text-slate-900">
                        Teeth - {tooth.id}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updatedTeeth = selectedTeeth.filter(
                          (t) => t.id !== tooth.id
                        );
                        setSelectedTeeth(updatedTeeth);
                      }}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                      aria-label={`Remove tooth ${tooth.id}`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* START: improved treatment reasons card */}
                  <div className="space-y-3">
                    {/* Card header with blue badge and close icon (you can wire the close to a remove-tooth handler) */}
                    <div className="flex items-center justify-between">
                      {/* <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold shadow-sm">
                          {tooth?.id}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-700">Tooth {tooth?.id}</div>
                          <div className="text-xs text-slate-400">Treatment Reasons</div>
                        </div>
                      </div> */}

                      {/* close / remove icon (optional) */}
                      {/* <button
                        onClick={() => {
                          // if you want to remove the entire tooth from selection:
                          setSelectedTeeth((prev) => prev.filter((t) => t.id !== tooth.id));
                        }}
                        className="text-slate-400 hover:text-slate-600 p-1 rounded-full"
                        title="Remove tooth"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button> */}
                    </div>

                    {/* outer card */}
                    <div className="bg-white border border-slate-100 rounded-lg ">
                      {/* inner panel (rounded, subtle border like your mock) */}
                      <div className="bg-white border border-slate-100 rounded-lg p-4">
                        {/* top row: diagnosis, treatment, count */}


                        {/* main reason(s) row: show larger primary pills first, then yellow chips */}
                        <div className="flex flex-col gap-3">
                          {/* Example: primary/featured reasons (bigger pill style) */}
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="text-xs text-slate-500">O/E Findings: </div>
                            {/* If you have a concept of a primary reason, show it bigger; here we show the first reason bigger */}
                            {Array.isArray(tooth?.reasons) && tooth.reasons.length > 0 ? (
                              <>


                                {/* remaining reasons shown as smaller yellow chips */}
                                <div className="flex flex-wrap gap-2">

                                  {tooth.reasons.map((reason, idx) => (
                                    <div
                                      key={`${tooth.id}-chip-${reason}-${idx}`}
                                      className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium"
                                    >
                                      <span className="text-xs">{reason}</span>
                                      <button
                                        onClick={() =>
                                          setSelectedTeeth((prev) =>
                                            prev.map((t) =>
                                              t.id === tooth.id
                                                ? { ...t, reasons: (t.reasons || []).filter((r) => r !== reason) }
                                                : t
                                            )
                                          )
                                        }
                                        className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-amber-100"
                                        aria-label={`Remove ${reason}`}
                                      >
                                        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <div className="text-xs text-slate-500 flex items-center gap-2">
                                <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                No treatment reasons specified
                              </div>
                            )}
                          </div>

                          {/* If you want to show all reasons as uniform chips instead of primary + detail chips,
            you can easily switch to mapping tooth.reasons.map here. */}
                        </div>

                        <div className="flex items-center justify-between mt-4 gap-4">
                          <div className="flex flex-col  gap-4 flex-wrap">
                            <div className="text-xs text-slate-500">Diagnosis:
                              <span className="px-3 py-1 rounded-full bg-slate-50 border border-slate-100 text-sm font-semibold text-slate-700">
                                {tooth?.diagnosis || "—"}
                              </span>
                            </div>

                            <div className="text-xs text-slate-500">Treatment:
                              <span className="px-3 py-1 rounded-full bg-slate-50 border border-slate-100 text-sm font-semibold text-slate-700">
                                {tooth?.treatment || "—"}
                              </span>
                            </div>

                          </div>

                          {/* reasons count */}
                          <div className="text-xs text-slate-400 text-right">

                            {Array.isArray(tooth?.reasons) && tooth.reasons.length > 0
                              ? `${tooth.reasons.length} reason${tooth.reasons.length > 1 ? "s" : ""}`
                              : "0 reasons"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* END: improved treatment reasons card */}


                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h4 className="text-slate-900 font-medium mb-2">
                No teeth selected
              </h4>
              <p className="text-slate-500 text-sm">
                Use the dental chart above to select teeth that require
                treatment
              </p>
            </div>
          )}

          {selectedTeeth.length > 0 && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-slate-600">Total teeth selected:</span>
                  <span className="font-semibold text-slate-900">
                    {selectedTeeth.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-600">
                    With treatment reasons:
                  </span>
                  <span className="font-semibold text-slate-900">
                    {
                      selectedTeeth.filter(
                        (tooth) => tooth.reasons && tooth.reasons.length > 0
                      ).length
                    }
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-8 p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-semibold text-slate-900">
                  {opdForm?.userDetails?.name}
                </h1>
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-full ${opdForm?.user?.status === "active" ? "bg-green-500" : "bg-red-500"
                    }`}
                  title={opdForm?.user?.status === "active" ? "Active" : "Inactive"}
                ></span>
              </div>

              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span className="font-medium text-blue-600">OPD #{patientReport.opdNumber}</span>
                <span>•</span>
                <span>{opdForm?.userDetails?.gender} • {opdForm?.age} yrs</span>
                <span>•</span>
                <span>{opdForm?.medicalDetails?.bloodGroup || "N/A"}</span>
                <span>•</span>
                <span className="capitalize">{opdForm?.status || "Open"}</span>
              </div>

              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                <span>📞 {opdForm?.userDetails?.contact?.mobile}</span>
                <span>✉️ {opdForm?.userDetails?.contact?.email}</span>
              </div>
            </div>

            <div className="text-right space-y-1">
              <div>
                <p className="text-xs text-slate-500">Created</p>
                <p className="text-sm text-slate-700 font-medium">
                  {patientReport.createdAt
                    ? (() => {
                      const date = new Date(patientReport.createdAt);
                      const day = String(date.getDate()).padStart(2, "0");
                      const month = String(date.getMonth() + 1).padStart(2, "0");
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()
                    : (() => {
                      const today = new Date();
                      const day = String(today.getDate()).padStart(2, "0");
                      const month = String(today.getMonth() + 1).padStart(2, "0");
                      const year = today.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()}
                </p>
              </div>

              <div className="pt-2 border-t border-slate-200">
                <p className="text-xs text-slate-500">Chief Complaint</p>
                <p className="text-sm text-slate-700 font-medium max-w-[200px] truncate" title={opdForm?.checkupInfo}>
                  {opdForm?.checkupInfo || "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {initialLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loading />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Patient Overview */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Patient Overview
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    OPD Number
                  </label>
                  <input
                    type="text"
                    value={patientReport.opdNumber}
                    readOnly
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Chief Complaint
                  </label>
                  <input
                    type="text"
                    value={opdForm?.checkupInfo || "Not specified"}
                    readOnly
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-600"
                  />
                </div>

                {/* Medical History */}
                {/* {console.log(
                  "opdForm:",
                  opdForm?.medicalDetails?.medicalHistory
                )} */}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Medical History
                  </label>
                  <input
                    type="text"
                    value={
                      opdForm?.medicalDetails?.medicalHistory?.join(", ") ||
                      "Not specified"
                    }
                    readOnly
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-600"
                  />
                </div>

                {/* Medications */}

                {/* <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                   Current Medications <span className="text-red-500">*</span>
                  </label>
                  <Select
                    isMulti
                    options={medicationOptions}
                    value={patientReport.examinationFindings?.currentMedications || []}
                    onChange={handleMedicationSelect}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 "
                    classNamePrefix="react-select"
                    placeholder="Choose medications..."
                  />
                </div> */}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Current Medications
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategory("medicationOptions");
                        setIsOpen(!isOpen)
                      }}

                      className="ml-2 text-xs text-blue-600 underline">
                      add
                    </button>
                  </label>

                  {console.log("medicationOptions:", medicationOptions)}

                  <Select
                    isMulti
                    options={medicationOptions.map(x => ({ value: x, label: x }))}
                    value={
                      patientReport.examinationFindings?.currentMedications ||
                      []
                    }
                    onChange={handleCurrentMedicationSelect}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    placeholder="Choose current medications..."
                    isClearable
                    isSearchable
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: "44px",
                        borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
                        boxShadow: state.isFocused
                          ? "0 0 0 2px rgba(59, 130, 246, 0.2)"
                          : "none",
                        "&:hover": {
                          borderColor: "#94a3b8",
                        },
                      }),
                      multiValue: (provided) => ({
                        ...provided,
                        backgroundColor: "#eff6ff",
                        borderRadius: "6px",
                      }),
                      multiValueLabel: (provided) => ({
                        ...provided,
                        color: "#1e40af",
                        fontWeight: "500",
                      }),
                      multiValueRemove: (provided) => ({
                        ...provided,
                        color: "#64748b",
                        "&:hover": {
                          backgroundColor: "#fee2e2",
                          color: "#dc2626",
                        },
                      }),
                    }}
                  />

                  {/* Display selected medications count */}
                  {patientReport.examinationFindings?.currentMedications
                    ?.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-slate-600">
                          {
                            patientReport.examinationFindings.currentMedications
                              .length
                          }{" "}
                          medication(s) selected
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Allergies Section */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-red-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Allergies to
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={addAllergy}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors border border-red-200"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                  </svg>
                  Add Allergy
                </button>
              </div>

              {/* Show allergies table only if there are allergies added */}
              {patientReport.examinationFindings?.allergies &&
                patientReport.examinationFindings.allergies.length > 0 ? (
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  }
                >
                  <AllergiesSection
                    allergies={patientReport.examinationFindings.allergies}
                    onChange={handleAllergiesChange}
                    onAdd={addAllergy}
                    onRemove={removeAllergy}
                  />
                </Suspense>
              ) : (
                // Show this when no allergies are added
                <div className="text-center py-8 text-slate-500 text-sm">
                  Click "Add Allergy" to document patient allergies.
                </div>
              )}
            </div>

            {/* Clinical Examination */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-emerald-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Clinical Examination
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Gums Condition */}

                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Gums Condition <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      {
                        value: "Healthy gum",
                        label: "Healthy Gum",
                        color: "green",
                      },
                      {
                        value: "Bleeding gum",
                        label: "Bleeding Gum",
                        color: "red",
                      },
                      {
                        value: "Swollen Gum",
                        label: "Swollen Gum",
                        color: "orange",
                      },
                    ].map((condition) => (
                      <label
                        key={condition.value}
                        className={`
          flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all duration-200
          ${patientReport.examinationFindings.gumsCondition === condition.value
                            ? `border-${condition.color}-200 bg-${condition.color}-50 ring-2 ring-${condition.color}-200`
                            : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                          }
        `}
                      >
                        <input
                          type="radio"
                          name="gumsCondition"
                          value={condition.value}
                          checked={
                            patientReport.examinationFindings.gumsCondition ===
                            condition.value
                          }
                          onChange={(e) =>
                            handleNestedChange(
                              e,
                              "examinationFindings",
                              "gumsCondition"
                            )
                          }
                          required
                          className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                        />
                        <div className="flex items-center gap-2">
                          {/* Status Icon */}

                          <div>
                            <span
                              className={`
              text-sm font-medium
              ${patientReport.examinationFindings.gumsCondition ===
                                  condition.value
                                  ? condition.color === "green"
                                    ? "text-green-700"
                                    : condition.color === "red"
                                      ? "text-red-700"
                                      : "text-orange-700"
                                  : "text-slate-700"
                                }
            `}
                            >
                              {condition.label}
                            </span>
                            <p className="text-xs text-slate-500 mt-1">
                              {condition.value === "Healthy gum"}
                              {condition.value === "Bleeding gum"}
                              {condition.value === "Swollen Gum"}
                            </p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Oral Hygiene Level */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Oral Hygiene Level <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-5 gap-3">
                    {["Very Poor", "Poor", "Fair", "Good", "Excellent"].map(
                      (level) => (
                        <label
                          key={level}
                          className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                        >
                          <input
                            type="radio"
                            name="oralHygieneLevel"
                            value={level}
                            checked={
                              patientReport.examinationFindings
                                .oralHygieneLevel === level
                            }
                            onChange={(e) =>
                              handleNestedChange(
                                e,
                                "examinationFindings",
                                "oralHygieneLevel"
                              )
                            }
                            required
                            className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">
                            {level}
                          </span>
                        </label>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Dental Chart */}
              <div className="mt-8 space-y-4">
                <label className="text-sm font-medium text-slate-700">
                  Dental Chart
                </label>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <ToothGrid
                    selectedTeeth={selectedTeeth}
                    setSelectedTeeth={setSelectedTeeth}
                    patientAge={opdForm?.age}
                  />
                </div>
                {renderSelectedTeethWithReasons()}
              </div>

              {/* Clinical Notes */}
              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="enableClinicalNotes"
                    checked={enableClinicalNotes}
                    onChange={(e) => {
                      setEnableClinicalNotes(e.target.checked);
                      // Clear notes if unchecked
                      if (!e.target.checked) {
                        setPatientReport((prev) => ({
                          ...prev,
                          examinationFindings: {
                            ...prev.examinationFindings,
                            notes: "",
                          },
                        }));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label
                    htmlFor="enableClinicalNotes"
                    className="text-sm font-medium text-slate-700 cursor-pointer select-none"
                  >
                    Add Clinical Notes
                  </label>
                </div>

                {enableClinicalNotes && (
                  <textarea
                    value={patientReport.examinationFindings.notes || ""}
                    onChange={(e) =>
                      handleNestedChange(e, "examinationFindings", "notes")
                    }
                    rows="4"
                    placeholder="Additional examination findings and observations..."
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200"
                  />
                )}
              </div>
            </div>

            {/* Diagnosis */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-amber-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  General Diagnosis
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Primary Diagnosis <span className="text-red-500">*</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategory("primaryDiagnosis");
                        setIsOpen(!isOpen)
                      }}

                      className="ml-2 text-xs text-blue-600 underline">
                      add
                    </button>
                  </label>
                  <Dropdown
                    item={"PrimaryDiagnosis"} // Change from 'item' to 'selectedUser'
                    selectedOption={patientReport.diagnosis.primaryDiagnosis}
                    onChange={(e) => {
                      setPatientReport((prev) => ({
                        ...prev,
                        diagnosis: {
                          ...prev.diagnosis,
                          primaryDiagnosis: e.target.value,
                        },
                      }));
                    }}
                    listOptions={primaryDiagnosis}
                    isRequired={true}
                  />

                  {/* <select
                    value={patientReport.diagnosis.primaryDiagnosis || ""}
                    onChange={(e) =>
                      handleNestedChange(e, "diagnosis", "primaryDiagnosis")
                    }
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="" disabled>
                      Select primary diagnosis...
                    </option>
                    {primaryDiagnosis.map((diagnosis, index) => (
                      <option key={index} value={diagnosis.Diagnosis}>
                        {diagnosis.Diagnosis}
                      </option>
                    ))}
                  </select> */}
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Secondary Diagnosis
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategory("secondaryDiagnosis");
                        setIsOpen(!isOpen)
                      }}

                      className="ml-2 text-xs text-blue-600 underline">
                      add
                    </button>
                  </label>
                  {/* <input
                    type="text"
                    value={patientReport.diagnosis.secondaryDiagnosis || ""}
                    onChange={(e) =>
                      handleNestedChange(e, "diagnosis", "secondaryDiagnosis")
                    }
                    placeholder="Additional diagnosis (optional)"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  /> */}
                  <Dropdown
                    item={"SecondaryDiagnosis"} // Change from 'item' to 'selectedUser'
                    selectedOption={patientReport.diagnosis.secondaryDiagnosis}
                    onChange={(e) => {
                      setPatientReport((prev) => ({
                        ...prev,
                        diagnosis: {
                          ...prev.diagnosis,
                          secondaryDiagnosis: e.target.value,
                        },
                      }));
                    }}
                    listOptions={secondaryDiagnosis}
                  />
                </div>
              </div>
            </div>

            {/* Treatment Plan */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-purple-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Treatment Plan

                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Recommended Treatment{" "}
                    <span className="text-red-500">*</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategory("treatmentPlan");
                        setIsOpen(!isOpen)
                      }}

                      className="ml-2 text-xs text-blue-600 underline">
                      add
                    </button>
                  </label>

                  <Dropdown
                    item={"Treatment Plan"} // Change from 'item' to 'selectedUser'
                    selectedOption={
                      patientReport.treatmentPlan.treatmentSuggested
                    }
                    onChange={(e) => {
                      setPatientReport((prev) => ({
                        ...prev,
                        treatmentPlan: {
                          ...prev.treatmentPlan,
                          treatmentSuggested: e.target.value,
                        },
                      }));
                    }}
                    listOptions={treatmentPlan.map(tp => tp.item)}
                  />

                  {/* <select
                    value={patientReport.treatmentPlan.treatmentSuggested || ""}
                    onChange={(e) =>
                      handleNestedChange(
                        e,
                        "treatmentPlan",
                        "treatmentSuggested"
                      )
                    }
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="" disabled>
                      Select treatment plan...
                    </option>
                    {treatmentPlan.map((treatment, index) => (
                      <option key={index} value={treatment}>
                        {treatment}
                      </option>
                    ))}
                  </select> */}
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Procedures Performed Today
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategory("proceduresPerformedToday");
                        setIsOpen(!isOpen)
                      }}

                      className="ml-2 text-xs text-blue-600 underline">
                      add
                    </button>
                  </label>
                  {/* <input
                    type="text"
                    value={
                      patientReport.treatmentPlan
                        .immediateProceduresPerformed || ""
                    }
                    onChange={(e) =>
                      handleNestedChange(
                        e,
                        "treatmentPlan",
                        "immediateProceduresPerformed"
                      )
                    }
                    placeholder="Procedures completed during this visit"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  /> */}

                  <Dropdown
                    item={"Treatment Plan"} // Change from 'item' to 'selectedUser'
                    selectedOption={
                      patientReport.treatmentPlan
                        .immediateProceduresPerformed
                    }
                    onChange={(e) => {
                      setPatientReport((prev) => ({
                        ...prev,
                        treatmentPlan: {
                          ...prev.treatmentPlan,
                          immediateProceduresPerformed: e.target.value,
                        },
                      }));
                    }}
                    listOptions={proceduresPerformedToday}
                  />
                </div>
              </div>
            </div>

            {/* 1. PRESCRIPTION SECTION */}

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-blue-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Prescription
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={addMedication}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                  </svg>
                  Add Medication
                </button>
              </div>

              {patientReport.treatmentPlan.prescribedMedications &&
                patientReport.treatmentPlan.prescribedMedications.length > 0 ? (
                <div className="space-y-4 border border-slate-200 rounded-lg">
                  {/* Table Header */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-slate-100 rounded-lg font-medium text-center">
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Medication Name
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCategory("medicineName");
                          setIsOpen(!isOpen)
                        }}

                        className="ml-2 text-xs text-blue-600 underline">
                        add
                      </button>
                    </div>

                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Timing
                    </div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Frequency
                    </div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Duration (Days)
                    </div>
                  </div>

                  {/* Table Rows */}
                  <div className="space-y-2">
                    {patientReport.treatmentPlan.prescribedMedications.map(
                      (med, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-white hover:bg-slate-50 transition-colors"
                        >
                          <div>
                            {/* <input
                              type="text"
                              placeholder="Medicine name"
                              value={med.name}
                              onChange={(e) =>
                                handleMedicationChange(e, index, "name")
                              }
                              required 
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            /> */}
                            <Dropdown
                              item="MedicineName"
                              selectedOption={med.name || ""}
                              onChange={(e) =>
                                handleMedicationChange(e, index, "name")
                              }
                              listOptions={medicineName}
                            />
                          </div>

                          <div className="flex justify-center">
                            <div className="flex flex-col gap-2">
                              <label className="flex items-center whitespace-nowrap">
                                <input
                                  type="radio"
                                  name={`mealTiming-${index}`}
                                  value="before_meal"
                                  checked={med.mealTiming === "before_meal"}
                                  onChange={(e) =>
                                    handleMedicationChange(
                                      e,
                                      index,
                                      "mealTiming"
                                    )
                                  }
                                  className="mr-2 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-slate-700">
                                  Before Meal
                                </span>
                              </label>
                              <label className="flex items-center whitespace-nowrap">
                                <input
                                  type="radio"
                                  name={`mealTiming-${index}`}
                                  value="after_meal"
                                  checked={med.mealTiming === "after_meal"}
                                  onChange={(e) =>
                                    handleMedicationChange(
                                      e,
                                      index,
                                      "mealTiming"
                                    )
                                  }
                                  className="mr-2 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-slate-700">
                                  After Meal
                                </span>
                              </label>
                            </div>
                          </div>
                          <div className="flex items-center justify-center">
                            <div className="flex flex-col gap-3 justify-center">
                              <label className="flex items-center whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={
                                    med.timeOfDay?.includes("morning") || false
                                  }
                                  onChange={(e) => {
                                    const currentTimes = med.timeOfDay || [];
                                    const newTimes = e.target.checked
                                      ? [...currentTimes, "morning"]
                                      : currentTimes.filter(
                                        (time) => time !== "morning"
                                      );
                                    handleMedicationChange(
                                      { target: { value: newTimes } },
                                      index,
                                      "timeOfDay"
                                    );
                                  }}
                                  className="mr-2 text-blue-600 focus:ring-blue-500 rounded"
                                />
                                <span className="text-sm text-slate-700">
                                  Morning
                                </span>
                              </label>
                              <label className="flex items-center whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={
                                    med.timeOfDay?.includes("afternoon") ||
                                    false
                                  }
                                  onChange={(e) => {
                                    const currentTimes = med.timeOfDay || [];
                                    const newTimes = e.target.checked
                                      ? [...currentTimes, "afternoon"]
                                      : currentTimes.filter(
                                        (time) => time !== "afternoon"
                                      );
                                    handleMedicationChange(
                                      { target: { value: newTimes } },
                                      index,
                                      "timeOfDay"
                                    );
                                  }}
                                  className="mr-2 text-blue-600 focus:ring-blue-500 rounded"
                                />
                                <span className="text-sm text-slate-700">
                                  Afternoon
                                </span>
                              </label>
                              <label className="flex items-center whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={
                                    med.timeOfDay?.includes("evening") || false
                                  }
                                  onChange={(e) => {
                                    const currentTimes = med.timeOfDay || [];
                                    const newTimes = e.target.checked
                                      ? [...currentTimes, "evening"]
                                      : currentTimes.filter(
                                        (time) => time !== "evening"
                                      );
                                    handleMedicationChange(
                                      { target: { value: newTimes } },
                                      index,
                                      "timeOfDay"
                                    );
                                  }}
                                  className="mr-2 text-blue-600 focus:ring-blue-500 rounded"
                                />
                                <span className="text-sm text-slate-700">
                                  Evening
                                </span>
                              </label>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <input
                              type="number"
                              placeholder="e.g., 7"
                              value={med.durationDays || ""}
                              onChange={(e) =>
                                handleMedicationChange(e, index, "durationDays")
                              }
                              min="1"
                              onWheel={(e) => e.target.blur()}
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <button
                              type="button"
                              onClick={() => removeMedication(index)}
                              className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                              title="Remove medication"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 text-sm">
                  No medications prescribed yet. Click "Add Medication" to start
                  creating the prescription.
                </div>
              )}
            </div>

            {/* 2. COST ESTIMATION SECTION */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-green-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.51-1.31c-.562-.649-1.413-1.076-2.353-1.253V5z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Treatment Cost Estimation
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={addBillingItem}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors border border-green-200"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                  </svg>
                  Add Item
                </button>
              </div>

              {patientReport.financials.billingItems &&
                patientReport.financials.billingItems.length > 0 ? (
                <div className="space-y-4 border border-slate-200 rounded-lg">
                  {/* Table Header */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-slate-100 rounded-lg font-medium">
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Procedure
                      {/* <button
                        type="button"
                        onClick={() => {
                          setSelectedCategory("medicineName");
                          setIsOpen(!isOpen)
                        }}

                        className="ml-2 text-xs text-blue-600 underline">
                        add
                      </button> */}
                    </div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Quantity
                    </div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Unit Cost
                    </div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Total
                    </div>
                  </div>

                  <div className="space-y-2">

                    {/* <BillingItems
        treatmentPlan={treatmentPlan}
        patientReport={patientReport}
        setPatientReport={setPatientReport}
      /> */}
                  </div>

                  {/* Table Rows */}
                  <div className="space-y-2">
                    {patientReport.financials.billingItems.map(
                      (item, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-white hover:bg-slate-50 transition-colors"
                        >
                          <div>
                            <Dropdown
                              item={"Procedure"}
                              selectedOption={
                                item.name
                              }

                              onChange={(e) =>
                                handleBillingItemChange(
                                  e,
                                  index,
                                  "name"
                                )
                              }
                              listOptions={treatmentPlan.map(tp => tp.item)}
                            />
                            {/* {console.log("treatmentPlan in billing:", treatmentPlan.map(tp => tp.item))} */}

                            {/* <select
                              value={item.name}
                              onChange={(e) =>
                                handleBillingItemChange(e, index, "name")
                              }
                              required
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="" disabled>
                                Select treatment plan...
                              </option>
                              {treatmentPlan.map((treatment, idx) => (
                                <option key={idx} value={treatment}>
                                  {treatment}
                                </option>
                              ))}
                            </select> */}
                          </div>

                          <div>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onWheel={(e) => e.target.blur()}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) =>
                                handleBillingItemChange(e, index, "quantity")
                              }
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.pricePerItem}
                              onWheel={(e) => e.target.blur()}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) =>
                                handleBillingItemChange(
                                  e,
                                  index,
                                  "pricePerItem"
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <input
                              type="number"
                              value={item.totalCostPerItem}
                              readOnly
                              onWheel={(e) => e.target.blur()}
                              className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-600 font-medium"
                            />
                            <button
                              type="button"
                              onClick={() => removeBillingItem(index)}
                              className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                              title="Remove item"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 text-sm">
                  No billing items added yet. Click "Add Item" to start building
                  the cost estimation.
                </div>
              )}

              {/* Total Cost */}
              {patientReport.financials.billingItems &&
                patientReport.financials.billingItems.length > 0 && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-slate-700">
                        Total Estimated Cost:
                      </span>
                      <span className="text-2xl font-bold text-blue-700">
                        ₹
                        {patientReport?.financials?.totalCost?.toLocaleString() ||
                          0}
                      </span>
                    </div>
                  </div>
                )}
            </div>

            {/* 3. TREATMENT SESSIONS SECTION */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-indigo-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Patients Visits
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={addVisit}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                  </svg>
                  Add Session
                </button>
              </div>
              {/* Only show input fields if there are visits */}
              {patientReport.financials.visits &&
                patientReport.financials.visits.length > 0 ? (
                <div className="space-y-4">
                  {patientReport.financials.visits.map((visit, index) => (
                    <div
                      key={index}
                      className="p-4 border border-slate-200 rounded-lg"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-sm font-medium text-slate-700">
                          Session {index + 1}
                        </h3>
                        <button
                          type="button"
                          onClick={() => removeVisit(index)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Remove session"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600">
                            Treatment Completed
                          </label>
                          <div className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={visit.treatmentDone === true}
                                onChange={(e) =>
                                  handleNestedChange(
                                    { target: { value: e.target.checked } },
                                    "financials",
                                    "visits",
                                    index,
                                    "treatmentDone"
                                  )
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded mr-3"
                              />
                              <span
                                className={`text-sm ${visit.treatmentDone === true
                                  ? "text-green-600 font-medium"
                                  : "text-slate-500"
                                  }`}
                              >
                                {visit.treatmentDone === true
                                  ? "Yes, Done"
                                  : "No, Still not"}
                              </span>
                            </label>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600">
                            Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={
                              visit.date ? visit.date.substring(0, 10) : ""
                            }
                            onChange={(e) =>
                              handleNestedChange(
                                e,
                                "financials",
                                "visits",
                                index,
                                "date"
                              )
                            }
                            required
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600">
                            Time <span className="text-red-500">*</span>
                          </label>
                          <Dropdown
                            item="time"
                            selectedOption={visit.time}
                            onChange={(e) =>
                              handleNestedChange(
                                e,
                                "financials",
                                "visits",
                                index,
                                "time"
                              )
                            }
                            listOptions={timeBlocks}
                          />

                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600">
                            Purpose <span className="text-red-500">*</span>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCategory("visitReason");
                                setIsOpen(!isOpen)
                              }}

                              className="ml-2 text-xs text-blue-600 underline">
                              add
                            </button>
                          </label>

                          <Dropdown
                            item="VisitPurpose"
                            selectedOption={visit.reason || ""}
                            onChange={(e) =>
                              handleNestedChange(
                                e,
                                "financials",
                                "visits",
                                index,
                                "reason"
                              )
                            }
                            listOptions={visitReason}
                          />
                          {/* 
                          <select
                            value={visit.reason || ""}
                            onChange={(e) =>
                              handleNestedChange(
                                e,
                                "financials",
                                "visits",
                                index,
                                "reason"
                              )
                            }
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="" disabled>
                              Select purpose...
                            </option>
                            {visitReason.map((reason) => (
                              <option key={reason} value={reason}>
                                {reason}
                              </option>
                            ))}
                          </select> */}
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600">
                            Amount Paid <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            min="0"
                            value={visit.amount}
                            onChange={(e) =>
                              handleNestedChange(
                                e,
                                "financials",
                                "visits",
                                index,
                                "amount"
                              )
                            }
                            onFocus={(e) => e.target.select()}
                            required
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        {/* --- NEWLY ADDED SECTION --- */}
                        <div className="mt-4 pt-4 border-t border-slate-200 flex justify-end">
                          <PaymentMemoHTML
                            visit={visit}
                            opdForm={opdForm}
                            patientReport={patientReport}
                            index={index}
                          />
                        </div>
                        {/* --- END OF NEW SECTION --- */}
                      </div>
                      <div className="mt-4 space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Session Notes
                        </label>
                        <input
                          type="text"
                          placeholder="Additional notes for this session..."
                          value={visit.notes || ""}
                          onChange={(e) =>
                            handleNestedChange(
                              e,
                              "financials",
                              "visits",
                              index,
                              "notes"
                            )
                          }
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Show only when no visits exist
                <div className="text-center py-8">
                  <div className="text-slate-500 text-sm mb-4">
                    No treatment sessions recorded yet.
                  </div>
                  <p className="text-xs text-slate-400">
                    Click "Add Session" above to start recording treatment
                    visits.
                  </p>
                </div>
              )}

              {/* Financial Summary */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="flex justify-between items-center text-sm">
                  <div className="flex gap-6">
                    <div>
                      <span className="text-slate-600">Total Cost: </span>
                      <span className="font-medium text-slate-900">
                        ₹{patientReport?.financials?.totalCost || 0}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-600">Total Paid: </span>
                      <span className="font-medium text-green-600">
                        ₹
                        {patientReport?.financials?.visits?.reduce(
                          (sum, visit) =>
                            sum +
                            (visit.treatmentDone === true
                              ? parseFloat(visit.amount) || 0
                              : 0),
                          0
                        ) || 0}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span
                      className={`font-semibold ${(patientReport?.financials?.totalCost || 0) -
                        (patientReport?.financials?.visits?.reduce(
                          (sum, visit) =>
                            sum + (parseFloat(visit.amount) || 0),
                          0
                        ) || 0) >
                        0
                        ? "text-red-600"
                        : "text-green-600"
                        }`}
                    >
                      {(patientReport?.financials?.totalCost || 0) -
                        (patientReport?.financials?.visits?.reduce(
                          (sum, visit) => sum + (parseFloat(visit.amount) || 0),
                          0
                        ) || 0)}{" "}
                      /
                      {(patientReport?.financials?.totalCost || 0) -
                        (patientReport?.financials?.visits?.reduce(
                          (sum, visit) =>
                            sum +
                            (visit.treatmentDone === true
                              ? parseFloat(visit.amount) || 0
                              : 0),
                          0
                        ) || 0)}{" "}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. DOCUMENTS SECTION */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-orange-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Clinical Documents
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategory("documentName");
                        setIsOpen(!isOpen)
                      }}

                      className="ml-2 text-xs text-blue-600 underline">
                      add
                    </button>
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={addDocument}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                  </svg>
                  Add Document
                </button>
              </div>

              {/* Only show documents list if documents exist and array has items */}
              {patientReport.documents &&
                patientReport.documents.length > 0 && (
                  <div className="space-y-4">
                    {patientReport.documents.map((doc, index) => (
                      <div
                        key={index}
                        className="p-4 border border-slate-200 rounded-lg"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="text-sm font-medium text-slate-700">
                            Document {index + 1}
                          </h3>
                          <button
                            type="button"
                            onClick={() => removeDocument(index)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Remove document"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-xs font-medium text-slate-600">
                              Document Name

                            </label>

                            <Dropdown
                              item="DocumentName"
                              selectedOption={doc.name}
                              onChange={(e) =>
                                handleNestedChange(
                                  e,
                                  "documents",
                                  null,
                                  index,
                                  "name"
                                )
                              }
                              listOptions={documentName}
                            />

                            {/* Show text input only when "Others" is selected */}
                            {doc.name === "Others" && (
                              <input
                                type="text"
                                placeholder="Enter custom document name"
                                value={doc.customName || ""}
                                onChange={(e) =>
                                  handleNestedChange(
                                    e,
                                    "documents",
                                    null,
                                    index,
                                    "customName"
                                  )
                                }
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-2"
                              />
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="text-xs font-medium text-slate-600">
                              Upload Document
                            </label>
                            <input
                              type="file"
                              accept="application/pdf,image/*"
                              onChange={(e) => handleDocumentUpload(e, index)}
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            />
                          </div>
                        </div>
                        {doc.url && (
                          <div className="mt-3">
                            <Link
                              to={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                              </svg>
                              View Document
                            </Link>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>

            {/* Additional Notes */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-gray-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Additional Notes
                </h2>
              </div>

              <textarea
                name="additionalNotes"
                value={patientReport.additionalNotes || ""}
                onChange={handleSimpleChange}
                placeholder="Any additional observations, follow-up instructions, or special notes..."
                rows="4"
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Action Buttons */}
            <div className="fixed bottom-8 right-8 z-50 flex flex-col gap-4 ">
              <button
                type="submit"
                disabled={loading}
                className={`
      px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg
      hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200
      disabled:opacity-50 disabled:cursor-not-allowed
      flex items-center justify-center gap-2 whitespace-nowrap
    `}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" />
                    </svg>
                    Updating...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>

                  </>
                )}
              </button>

              <button
                onClick={generatePatientReportHTML}
                className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <Eye size={18} />

              </button>

              <button
                onClick={generateReport}
                className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-200 transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <Printer size={18} />

              </button>

              <PatientReportPDF
                patientReport={patientReport}
                opdForm={opdForm}
                showReport={showReport}
                setShowReport={setShowReport}
                generatePatientReportHTML={generatePatientReportHTML}
              />

              <BillingReportHTML
                patientReport={patientReport}
                opdForm={opdForm}
                userDetails={opdForm.userDetails}
                generateReport={generateReport}
                showInvoice={showInvoice}
                setShowInvoice={setShowInvoice}
              />
            </div>

            {/* Status Messages */}
            {/* {success && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-green-800 font-medium">{success}</p>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-red-800 font-medium">{error}</p>
                </div>
              </div>
            )} */}
          </form>
        )}
      </div>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover={false}
        theme="light"
        limit={3}
        enableMultiContainer={false}
      />
      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-amber-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                Unsaved Changes
              </h3>
            </div>

            <p className="text-slate-600 mb-6">
              You have unsaved changes in the patient record. What would you
              like to do?
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSaveAndExit}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleExitWithoutSaving}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Exit
              </button>
              <button
                onClick={handleCancelExit}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <AddCategoryItemModal
        isOpen={isOpen}
        selectedCategory={selectedCategory}
        onClose={() => setIsOpen(false)}
        onAdded=""
      />
    </div>
  );
};

export default CheckupScroll;
